'use client';

import { useEffect, useId, useState } from 'react';
import { openChat, openCall } from '@/components/integrations/Kodif/Actions';
import {
  ChatBubbleLeftEllipsisIcon,
  PhoneIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import CTAButton from '@/components/elements/CTAButton';
import { renderRte } from '@/utils/rte';
import {
  DEFAULT_TIME_ZONE,
  getTimesText,
  getTodaysSchedule,
  isScheduleUnavailable,
} from '@/utils/cxSchedule';

// Mirrors the accordion in components/blocks/faq/Faq.js.
const FaqAccordionItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  const id = useId();
  const headingId = `${id}-heading`;
  const panelId = `${id}-panel`;

  return (
    <div className="bg-warm-white">
      <button
        type="button"
        id={headingId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-6 p-4 text-left cursor-pointer"
      >
        <span className="font-worksans text-primary font-medium leading-snug">
          {question}
        </span>
        <span
          aria-hidden="true"
          className="shrink-0 text-primary leading-none size-4 text-center select-none"
        >
          {open ? <XMarkIcon strokeWidth={2.5} /> : <PlusIcon strokeWidth={2.5} />}
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={headingId}
        inert={!open}
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div
          className="rte-content rte-content--light px-4 pb-4 font-worksans text-primary font-light leading-relaxed [&_p]:mb-3 last:[&_p]:mb-0"
          dangerouslySetInnerHTML={{ __html: renderRte(answer) }}
        />
      </div>
    </div>
  );
};

const V2CustomerServiceChat = ({ block }) => {
  const data = block || {};

  const { settings } = useSiteSettings();
  const uid = data?.uid ?? data?._metadata?.uid ?? 'customer-service-chat';
  const scheduleSource = settings?.cxHours;

  const [userTimeZone, setUserTimeZone] = useState(null);
  const [userLocale, setUserLocale] = useState(null);
  const isMounted = userTimeZone !== null;

  useEffect(() => {
    if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
      const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimeZone(resolvedTimeZone || DEFAULT_TIME_ZONE);
    } else {
      setUserTimeZone(DEFAULT_TIME_ZONE);
    }
    setUserLocale(typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US');
  }, []);

  const getSchedule = (serviceType) =>
    getTodaysSchedule(
      scheduleSource,
      userTimeZone || DEFAULT_TIME_ZONE,
      serviceType,
      userLocale || 'en-US',
      'V2CustomerServiceChat',
    );

  const channels = [
    {
      type: 'chat',
      label: 'Chat Now',
      variant: 'solid',
      icon: <ChatBubbleLeftEllipsisIcon aria-hidden="true" className="size-5" />,
      onClick: openChat,
    },
    {
      type: 'call',
      label: 'Call Now',
      variant: 'solid',
      icon: <PhoneIcon aria-hidden="true" className="size-5" />,
      onClick: () => openCall(data.telephone_number),
    },
  ];

  // BL-823: the "Chat Only Mobile" CMS toggle collapses this block down to a
  // single "Have a question? / Chat Now" row on anything narrower than the
  // two-column buy box. The cut is at `lg` on purpose — that is where
  // BuyBoxV2's grid splits, and BL-823's companion ticket calls out tablet
  // rendering nothing as the bug, so tablet has to get the compact row too,
  // not the full widget and not a gap.
  const chatOnlyMobile = Boolean(data.chat_only_mobile);

  // BL-823: chat is never disabled by the CX schedule, at any width — an AI
  // bot answers when the team is offline, so there is always a response. Call
  // still honours the schedule; the phone line has no after-hours fallback.
  const isChannelDisabled = (type, schedule) =>
    type === 'chat' ? false : !isMounted || isScheduleUnavailable(schedule);

  return (
    // No border: blocks separate from the warm-white column by their white
    // ground, matching buy_box_card (BL-652).
    // BL-784: comp specs square corners (verified via Figma MCP node
    // 4300:22292 — no rounded-* class on the card).
    <section
      id={uid}
      data-block="v2_customer_service_chat"
      className="flex flex-col gap-4 bg-white p-3 min-[360px]:p-4 md:p-6 font-worksans text-primary"
    >
      {chatOnlyMobile && (
        <div className="flex items-center justify-between gap-4 lg:hidden">
          <div className="flex items-center gap-3 min-w-0">
            <QuestionMarkCircleIcon aria-hidden="true" className="size-6 shrink-0" />
            <span className="text-base font-medium leading-normal">Have a question?</span>
          </div>
          {/* Deliberately never disabled by the CX schedule: an AI bot answers
              chat when the team is offline, so out-of-hours visitors still get
              a response (BL-823 review). */}
          <button
            type="button"
            onClick={openChat}
            className="shrink-0 text-lg font-normal leading-6 text-sea underline underline-offset-4 cursor-pointer"
          >
            Chat Now
          </button>
        </div>
      )}

      {/* `contents` keeps the default (non-toggled) layout byte-identical:
          the wrapper adds no box, so the section's own gap-4 still applies
          between the blocks below. */}
      <div className={chatOnlyMobile ? 'hidden lg:contents' : 'contents'}>
        <div className="flex flex-col text-xl leading-6">
          {data.section_heading && (
            <div className="text-base md:text-xl font-medium leading-normal">{data.section_heading}</div>
          )}
          {data.description && (
            <div className="text-sm font-light mt-1">{data.description}</div>
          )}

          <div className="flex gap-2 min-[360px]:gap-4 mt-4 w-full">
            {channels.map((channel) => {
              const schedule = getSchedule(channel.type);

              return (
                <div key={channel.type} className="flex flex-col gap-2 flex-1 min-w-0">
                  <CTAButton
                    variant={channel.variant}
                    radius="rounded-[40px]"
                    icon={channel.icon}
                    disabled={isChannelDisabled(channel.type, schedule)}
                    onClick={channel.onClick}
                    className={`w-full text-base leading-6 py-4 ${channel.extraClass ?? ''}`}
                  >
                    {channel.label}
                  </CTAButton>

                  {/* BL-803: Figma spec omits the availability dot ContactCX shows for v2 Buy Box. */}
                  <div className="text-xs tracking-tighter font-normal min-h-4 text-center min-[360px]:whitespace-nowrap">
                    {isMounted && schedule.isConfigured ? getTimesText(schedule) : '\u00A0'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {data.faqs?.length > 0 && (
          <div className="flex flex-col gap-1">
            {data.faqs.map((faq, index) => (
              <FaqAccordionItem
                key={faq?._metadata?.uid || index}
                question={faq?.question}
                answer={faq?.answer}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default V2CustomerServiceChat;
